import React from 'react';
import { Text, Title } from 'rizzui';
import WidgetCard from '../../../../../components/card/WidgetCard';
import { CustomBadge } from '../../../../../components/forms/CustomBadge';
import Icon from '../../../../../components/icon/Icon';
import cn from '../../../../../utils/classNames';

interface RankingCardProps {
  rankingName: string;
  rankingDescription: string;
  position: number;
  userNumbers: number;
  score: number;
  totalScore?: number;
  className?: string;
  onClick?: () => void;
}

//-------------------------------------------------------------------------------------------------------------------------------
/**
 * @ES Función para obtener el color del badge según la posición en el ranking.
 * @EN Function to get the badge color based on the ranking position.
 */
//-------------------------------------------------------------------------------------------------------------------------------
export const getPositionBadgeColor = (position: number): string => {
  if (position === 1) return '#FFD700'; // Gold
  if (position === 2) return '#C0C0C0'; // Silver
  if (position === 3) return '#CD7F32'; // Bronze
  return '#03B2CB'; // Primary
};
//-------------------------------------------------------------------------------------------------------------------------------

//-------------------------------------------------------------------------------------------------------------------------------
/**
 * @ES Función para obtener el icono según la posición en el ranking.
 * @EN Function to get the icon based on the ranking position.
 */
//-------------------------------------------------------------------------------------------------------------------------------
export const getPositionIcon = (position: number): string | undefined => {
  if (position === 1) return 'EmojiEvents'; // Trophy
  if (position === 2) return 'MilitaryTech'; // Medal
  if (position === 3) return 'Star'; // Star
  return undefined;
};
//-------------------------------------------------------------------------------------------------------------------------------


//-------------------------------------------------------------------------------------------------------------------------------
/**
 * @ES Componente de barra de progreso para mostrar la puntuación del usuario en el ranking.
 * @EN Progress bar component to display the user's score in the ranking.
 */
//-------------------------------------------------------------------------------------------------------------------------------
const ProgressBar: React.FC<{
  value: number;
  maxValue: number;
  className?: string;
}> = ({ value, maxValue, className }) => {
  const percentage = Math.min((value / maxValue) * 100, 100);

  return (
    <div className={cn("w-full bg-gray-200 rounded-full h-2", className)}>
      <div
        className="bg-gradient-to-r from-primary-50 to-primary-900 h-2 rounded-full transition-all duration-300 ease-in-out"
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
};
//-------------------------------------------------------------------------------------------------------------------------------


export default function RankingCard({
  rankingName,
  rankingDescription,
  position,
  userNumbers,
  score,
  totalScore,
  className,
  onClick
}: RankingCardProps) {
  const badgeColor = getPositionBadgeColor(position);
  const positionIcon = getPositionIcon(position);

  return (
    <div
      className={cn(
        "hover:shadow-lg transition-all duration-300 border-l-4 cursor-pointer",
        position === 1 && "border-l-amber-500",
        position === 2 && "border-l-blue-800",
        position === 3 && "border-l-green-600",
        position > 3 && "border-l-primary-500"
      )}
      onClick={onClick}
    >
      <WidgetCard
        title=""
        headerClassName="hidden"
        className={className}
      >
        <div className="space-y-4">
          {/* Header with name and position badge */}
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <Title
                as="h3"
                className="text-lg font-bold text-gray-900 truncate mb-1"
              >
                {rankingName}
              </Title>
              <Text className="text-sm text-gray-600 line-clamp-2">
                {rankingDescription}
              </Text>
            </div>

            <div className="flex items-center gap-2 ml-3">
              <CustomBadge
                customColor={badgeColor}
                variant="solid"
                size="lg"
                className="flex items-center gap-1 font-bold"
              >
                <Icon icon={positionIcon} className="w-5 h-5" />
                #{position}
              </CustomBadge>
            </div>
          </div>

          {/* Agent information */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Icon icon="Group" className="w-4 h-4 text-gray-500" />
              <Text className="text-gray-600">
                <span className="font-semibold text-gray-900">{position}</span> de{' '}
                <span className="font-semibold text-gray-900">{userNumbers}</span> {userNumbers === 1 ? 'agente' : 'agentes'}
              </Text>
            </div>

            <div className="flex items-center gap-1">
              <Icon icon="TrendingUp" className="w-4 h-4 text-green-500" />
              <Text className="text-sm font-medium text-gray-700">
                {score} pts
              </Text>
            </div>
          </div>

          {/* Progress bar for score */}
          {score && totalScore && (
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Text className="text-xs font-medium text-gray-600">
                  Puntuación actual
                </Text>
                <Text className="text-xs font-bold text-gray-900">
                  {score} / {totalScore}
                </Text>
              </div>
              <ProgressBar
                value={score}
                maxValue={totalScore}
                className="shadow-sm"
              />
            </div>
          )}

          {/* Performance indicator */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <div className={cn(
                "w-2 h-2 rounded-full",
                position <= 3 ? "bg-green-500" : position <= 10 ? "bg-yellow-500" : "bg-gray-400"
              )} />
              <Text className="text-xs text-gray-600">
                {position <= 3 ? "Top 3" : position <= 10 ? "Top 10" : "Participando"}
              </Text>
            </div>

            <Text className="text-xs text-gray-500">
              {((position / userNumbers) * 100).toFixed(1)}% percentil
            </Text>
          </div>
        </div>
      </WidgetCard>
    </div>
  );
}